/**
 * Pointing at a section of the invitation in the editor's preview
 * (pages/Preview.jsx): when the editor sends `focus-section`, scroll the
 * section under the nav and frame it for a moment. Also which line, if any,
 * the couple is typing on in there.
 *
 * Plain DOM, read at call time, so it can be checked without a browser
 * (previewFocus.test.js).
 */

const FOCUS_CLASS = "is-preview-focus";
const FOCUS_MARK_MS = 1600;

let markedSection = null;
let unmarkTimer;
// A request that came while the frame had no size, waiting for it to be shown.
let pendingKey = null;

const hasViewport = () => window.innerWidth > 0 && window.innerHeight > 0;

/**
 * The line of the invitation the couple is typing on, or null. hasFocus()
 * matters: a frame keeps its focused line after the couple clicks back into
 * the editor, and that line is no longer being typed on.
 */
export function lineBeingTyped() {
  if (!document.hasFocus()) return null;
  return document.activeElement?.closest?.("[data-invitation-slot]") ?? null;
}

/** Scroll a drawn section under the nav and frame it for a moment. */
export function focusSection(key) {
  // On a phone the editor hides its preview (display: none) while the couple
  // edits, and a hidden frame has no viewport to measure a section against.
  // Hold the request until the frame is shown (focusPendingSection).
  if (!hasViewport()) {
    pendingKey = key;
    return;
  }
  pendingKey = null;

  const section = [...document.querySelectorAll("[data-invitation-section]")].find(
    (el) => el.dataset.invitationSection === key,
  );
  if (!section) return;

  // The editor opens a line's section on the first keystroke typed on it, and
  // then points at that section. The couple is already where they want to be:
  // scrolling to the section's top would carry the line out of view mid-word.
  // A line left focused while the couple works in the editor doesn't count:
  // picking its section there must still scroll.
  const typedLine = lineBeingTyped();
  if (typedLine && section.contains(typedLine)) return;

  const navbar = document.querySelector(".navbar");
  const top =
    section.getBoundingClientRect().top + window.scrollY - (navbar ? navbar.offsetHeight : 0);
  window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });

  clearTimeout(unmarkTimer);
  markedSection?.classList.remove(FOCUS_CLASS);
  // Restart the frame's fade when the same section is pointed at again.
  void section.offsetWidth;
  section.classList.add(FOCUS_CLASS);
  markedSection = section;
  unmarkTimer = setTimeout(() => section.classList.remove(FOCUS_CLASS), FOCUS_MARK_MS);
}

/** Carry out a request held while the frame was hidden, once it has a size. */
export function focusPendingSection() {
  if (pendingKey !== null && hasViewport()) focusSection(pendingKey);
}

/** Take the frame down and drop any held request — the page is going away. */
export function clearPreviewFocus() {
  clearTimeout(unmarkTimer);
  markedSection?.classList.remove(FOCUS_CLASS);
  markedSection = null;
  pendingKey = null;
}
